import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { CameraView, useCameraPermissions, type CameraMountError } from 'expo-camera';
import type { CameraCapturedPicture } from 'expo-camera';
import * as FileSystem from 'expo-file-system/legacy';
import {
  acknowledgeInAppCamera,
  resolveInAppCamera,
  subscribeInAppCamera,
  type InAppCameraRequest,
} from '@/utils/media/inAppCamera';
import { alignWebCameraCapture } from '@/utils/media/webCameraCapture';
import { addSentryBreadcrumb, captureSentryException } from '@/services/monitoring/sentry';
import { ChecklistPhotoFieldLogger } from '@/utils/logger';

type CameraState = 'preview' | 'captured';

const CAMERA_READY_TIMEOUT_MS = 15_000;

const cameraFlowName = (): string =>
  Platform.OS === 'android' ? 'android_in_app_camera' : 'in_app_camera';

const toImagePickerAsset = (
  picture: CameraCapturedPicture
): import('expo-image-picker').ImagePickerAsset => ({
  uri: picture.uri,
  width: picture.width,
  height: picture.height,
  type: 'image',
  assetId: null,
  fileName: null,
  fileSize: undefined,
  mimeType: picture.format === 'png' ? 'image/png' : 'image/jpeg',
});

export const InAppCameraHost: React.FC = () => {
  const [request, setRequest] = useState<InAppCameraRequest | null>(null);
  const [permission, requestPermission] = useCameraPermissions();
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraState, setCameraState] = useState<CameraState>('preview');
  const [capturedPicture, setCapturedPicture] = useState<CameraCapturedPicture | null>(null);
  const [busy, setBusy] = useState(false);
  const cameraRef = useRef<React.ComponentRef<typeof CameraView> | null>(null);
  const permissionRequestInProgressRef = useRef(false);
  const requestRef = useRef<InAppCameraRequest | null>(null);
  const capturedPictureRef = useRef<CameraCapturedPicture | null>(null);
  const requestResolvedRef = useRef(false);
  const cameraReadyRef = useRef(false);
  const stageSizeRef = useRef({ width: 0, height: 0 });

  useEffect(() => {
    return subscribeInAppCamera(setRequest);
  }, []);

  const safeTelemetry = useCallback((input: Record<string, unknown> | undefined) => ({
    source: typeof input?.source === 'string' ? input.source : null,
    screen: typeof input?.screen === 'string' ? input.screen : null,
    action: typeof input?.action === 'string' ? input.action : null,
    entityType: typeof input?.entityType === 'string' ? input.entityType : null,
    platform: Platform.OS,
  }), []);

  const deleteCapturedPicture = useCallback(async (reason: string): Promise<void> => {
    const picture = capturedPictureRef.current;
    if (!picture?.uri) {
      return;
    }

    capturedPictureRef.current = null;
    setCapturedPicture(null);
    if (!picture.uri.startsWith('file://')) {
      ChecklistPhotoFieldLogger.Main.info(
        `[camera] arquivo temporário não removido (${reason}): URI não é file://`
      );
      return;
    }

    try {
      await FileSystem.deleteAsync(picture.uri, { idempotent: true });
      ChecklistPhotoFieldLogger.Main.info(`[camera] arquivo temporário removido (${reason})`);
    } catch (error) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      ChecklistPhotoFieldLogger.Main.warn(
        `[camera] falha ao remover arquivo temporário (${reason}): ${normalizedError.message}`
      );
      captureSentryException(normalizedError, {
        flow: cameraFlowName(),
        flowStage: 'temporary_file_cleanup',
        reason,
        ...safeTelemetry(requestRef.current?.telemetry),
      });
    }
  }, [safeTelemetry]);

  const resolveRequest = useCallback((
    requestId: string | undefined,
    result: Parameters<typeof resolveInAppCamera>[1]
  ) => {
    if (!requestId) {
      return;
    }
    requestResolvedRef.current = true;
    resolveInAppCamera(requestId, result);
  }, []);

  useEffect(() => {
    requestRef.current = request;
    if (!request) {
      requestResolvedRef.current = false;
      setCameraReady(false);
      setCameraState('preview');
      setCapturedPicture(null);
      capturedPictureRef.current = null;
      setBusy(false);
      return;
    }

    acknowledgeInAppCamera(request.requestId);
    requestResolvedRef.current = false;
    ChecklistPhotoFieldLogger.Main.info('[camera] modal interno montado');
    addSentryBreadcrumb({
      category: 'image_picker.camera',
      message: 'Câmera interna montada.',
      level: 'info',
      data: safeTelemetry(request.telemetry),
    });
  }, [request, safeTelemetry]);

  useEffect(() => () => {
    const activeRequest = requestRef.current;
    if (!activeRequest || requestResolvedRef.current) {
      return;
    }

    ChecklistPhotoFieldLogger.Main.warn('[camera] host desmontado durante captura; cancelando fluxo');
    void deleteCapturedPicture('host_unmounted');
    const error = new Error('Host da câmera interna desmontado durante a captura.');
    captureSentryException(error, {
      flow: cameraFlowName(),
      flowStage: 'host_unmounted',
      ...safeTelemetry(activeRequest.telemetry),
    });
    resolveRequest(activeRequest.requestId, { status: 'failed', error });
  }, [deleteCapturedPicture, resolveRequest, safeTelemetry]);

  useEffect(() => {
    if (!request || permission?.granted || permissionRequestInProgressRef.current) {
      return;
    }

    if (permission && !permission.canAskAgain) {
      ChecklistPhotoFieldLogger.Main.warn('[camera] permissão não concedida e não pode ser solicitada novamente');
      resolveRequest(request.requestId, { status: 'denied' });
      return;
    }

    permissionRequestInProgressRef.current = true;
    void requestPermission()
      .then(nextPermission => {
        if (!nextPermission.granted) {
          resolveRequest(request.requestId, { status: 'denied' });
        }
      })
      .catch(error => {
        const normalizedError = error instanceof Error ? error : new Error(String(error));
        ChecklistPhotoFieldLogger.Main.warn(`[camera] solicitação de permissão falhou: ${normalizedError.message}`);
        captureSentryException(normalizedError, {
          flow: cameraFlowName(),
          flowStage: 'permission_request',
          ...safeTelemetry(request?.telemetry),
        });
        resolveRequest(request.requestId, { status: 'failed', error: normalizedError });
      })
      .finally(() => {
        permissionRequestInProgressRef.current = false;
      });
  }, [permission, request, requestPermission, resolveRequest, safeTelemetry]);

  useEffect(() => {
    cameraReadyRef.current = cameraReady;
  }, [cameraReady]);

  useEffect(() => {
    if (!request || !permission?.granted || cameraState !== 'preview' || cameraReady) {
      return;
    }

    const timeout = setTimeout(() => {
      if (requestResolvedRef.current || cameraReadyRef.current) {
        return;
      }

      const error = new Error(
        'A câmera não ficou pronta a tempo. Verifique se há uma câmera disponível neste dispositivo.'
      );
      ChecklistPhotoFieldLogger.Main.warn(`[camera] ${error.message}`);
      captureSentryException(error, {
        flow: cameraFlowName(),
        flowStage: 'ready_timeout',
        ...safeTelemetry(request.telemetry),
      });
      resolveRequest(request.requestId, { status: 'failed', error });
    }, CAMERA_READY_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [cameraReady, cameraState, permission?.granted, request, resolveRequest, safeTelemetry]);

  const cancel = useCallback(async () => {
    if (busy) {
      return;
    }

    ChecklistPhotoFieldLogger.Main.info('[camera] captura cancelada pelo usuário');
    addSentryBreadcrumb({
      category: 'image_picker.camera',
      message: 'Captura interna cancelada pelo usuário.',
      level: 'info',
      data: safeTelemetry(request?.telemetry),
    });
    await deleteCapturedPicture('cancel');
    resolveRequest(request?.requestId, { status: 'cancelled' });
  }, [busy, deleteCapturedPicture, request, resolveRequest, safeTelemetry]);

  const capture = useCallback(async () => {
    if (!cameraRef.current || !cameraReady || busy || cameraState !== 'preview') {
      return;
    }

    setBusy(true);
    ChecklistPhotoFieldLogger.Main.info('[camera] captura iniciada');
    addSentryBreadcrumb({
      category: 'image_picker.camera',
      message: 'Captura interna iniciada.',
      level: 'info',
      data: safeTelemetry(request?.telemetry),
    });
    try {
      const quality = typeof request?.options.quality === 'number' ? request.options.quality : 0.7;
      const rawPicture = await cameraRef.current.takePictureAsync({
        quality,
        base64: false,
        exif: false,
        skipProcessing: false,
        imageType: 'jpg',
      });

      if (!rawPicture?.uri) {
        throw new Error('A câmera não retornou um arquivo de imagem.');
      }

      const picture = Platform.OS === 'web'
        ? await alignWebCameraCapture(rawPicture, stageSizeRef.current, quality)
        : rawPicture;

      capturedPictureRef.current = picture;
      setCapturedPicture(picture);
      setCameraState('captured');
      ChecklistPhotoFieldLogger.Main.info(
        `[camera] foto capturada (${picture.width}x${picture.height})`
      );
      addSentryBreadcrumb({
        category: 'image_picker.camera',
        message: 'Foto capturada na câmera interna.',
        level: 'info',
        data: {
          ...safeTelemetry(request?.telemetry),
          width: picture.width,
          height: picture.height,
          sourceWidth: rawPicture.width,
          sourceHeight: rawPicture.height,
        },
      });
    } catch (error) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      ChecklistPhotoFieldLogger.Main.warn(`[camera] captura falhou: ${normalizedError.message}`);
      captureSentryException(normalizedError, {
        flow: cameraFlowName(),
        flowStage: 'capture',
        ...safeTelemetry(request?.telemetry),
      });
      resolveRequest(request?.requestId, { status: 'failed', error: normalizedError });
    } finally {
      setBusy(false);
    }
  }, [busy, cameraReady, cameraState, request, resolveRequest, safeTelemetry]);

  const confirm = useCallback(() => {
    if (!capturedPicture || busy) {
      return;
    }

    ChecklistPhotoFieldLogger.Main.info('[camera] foto confirmada pelo usuário');
    addSentryBreadcrumb({
      category: 'image_picker.camera',
      message: 'Foto da câmera interna confirmada.',
      level: 'info',
      data: safeTelemetry(request?.telemetry),
    });
    capturedPictureRef.current = null;
    setCapturedPicture(null);
    resolveRequest(request?.requestId, {
      status: 'success',
      asset: toImagePickerAsset(capturedPicture),
    });
  }, [busy, capturedPicture, request, resolveRequest, safeTelemetry]);

  const retake = useCallback(async () => {
    if (busy) {
      return;
    }
    ChecklistPhotoFieldLogger.Main.info('[camera] usuário solicitou nova foto');
    addSentryBreadcrumb({
      category: 'image_picker.camera',
      message: 'Nova captura solicitada na câmera interna.',
      level: 'info',
      data: safeTelemetry(request?.telemetry),
    });
    setBusy(true);
    setCameraReady(false);
    setCameraState('preview');
    try {
      await deleteCapturedPicture('retake');
    } finally {
      setBusy(false);
    }
  }, [busy, deleteCapturedPicture, request, safeTelemetry]);

  if (!request) {
    return null;
  }

  const showCamera = permission?.granted && cameraState === 'preview';
  return (
    <Modal
      visible
      animationType="slide"
      presentationStyle="fullScreen"
      onRequestClose={cancel}
      statusBarTranslucent
    >
      <View style={styles.container}>
        <View style={styles.topBar}>
          <Pressable accessibilityLabel="Cancelar captura" onPress={cancel} style={styles.topButton}>
            <Text style={styles.topButtonText}>Cancelar</Text>
          </Pressable>
          <Text style={styles.title}>Tirar foto</Text>
          <View style={styles.topButtonPlaceholder} />
        </View>

        <View
          style={styles.stage}
          onLayout={event => {
            stageSizeRef.current = {
              width: event.nativeEvent.layout.width,
              height: event.nativeEvent.layout.height,
            };
          }}
        >
          {permission?.granted && cameraState === 'captured' && capturedPicture ? (
            <Image source={{ uri: capturedPicture.uri }} style={styles.preview} resizeMode="contain" />
          ) : showCamera ? (
            <CameraView
              ref={cameraRef}
              style={styles.camera}
              facing="back"
              mirror={Platform.OS === 'web'}
              flash="off"
              onCameraReady={() => {
                setCameraReady(true);
                ChecklistPhotoFieldLogger.Main.info('[camera] câmera pronta para captura');
                addSentryBreadcrumb({
                  category: 'image_picker.camera',
                  message: 'Câmera interna pronta.',
                  level: 'info',
                  data: safeTelemetry(request.telemetry),
                });
              }}
              onMountError={(error: CameraMountError) => {
                const normalizedError = new Error(error.message || 'Não foi possível iniciar a câmera.');
                ChecklistPhotoFieldLogger.Main.warn(`[camera] montagem falhou: ${normalizedError.message}`);
                captureSentryException(normalizedError, {
                  flow: cameraFlowName(),
                  flowStage: 'mount',
                  ...safeTelemetry(request.telemetry),
                });
                resolveRequest(request.requestId, { status: 'failed', error: normalizedError });
              }}
            />
          ) : (
            <View style={styles.loading}>
              <ActivityIndicator size="large" color="#fff" />
              <Text style={styles.loadingText}>
                {permission && !permission.granted ? 'Solicitando acesso à câmera…' : 'Abrindo câmera…'}
              </Text>
            </View>
          )}
        </View>

        <View style={styles.bottomBar}>
          {cameraState === 'captured' ? (
            <>
              <Pressable accessibilityLabel="Tirar outra foto" onPress={retake} style={[styles.actionButton, styles.retakeButton]}>
                <Text style={styles.actionText}>Tirar outra</Text>
              </Pressable>
              <Pressable accessibilityLabel="Usar foto" onPress={confirm} style={[styles.actionButton, styles.confirmButton]}>
                <Text style={styles.actionText}>Usar foto</Text>
              </Pressable>
            </>
          ) : (
            <Pressable
              accessibilityLabel="Tirar foto"
              onPress={() => void capture()}
              disabled={!cameraReady || busy}
              style={[styles.shutter, (!cameraReady || busy) && styles.shutterDisabled]}
            >
              <View style={styles.shutterInner} />
            </Pressable>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  stage: {
    ...(Platform.OS === 'web' ? { flex: 1 } : StyleSheet.absoluteFill),
    backgroundColor: '#000',
  },
  camera: {
    flex: 1,
  },
  preview: {
    flex: 1,
    width: '100%',
    backgroundColor: '#000',
  },
  loading: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    color: '#fff',
    marginTop: 12,
    fontSize: 16,
  },
  topBar: {
    zIndex: 1,
    ...(Platform.OS === 'web' ? {} : { position: 'absolute' as const, top: 0, left: 0, right: 0 }),
    paddingTop: Platform.OS === 'web' ? 16 : 52,
    paddingBottom: Platform.OS === 'web' ? 14 : 0,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: Platform.OS === 'web' ? '#000' : 'transparent',
  },
  title: {
    color: '#fff',
    fontSize: 17,
    fontWeight: '600',
  },
  topButton: {
    minWidth: 80,
    paddingVertical: 8,
  },
  topButtonPlaceholder: {
    width: 80,
  },
  topButtonText: {
    color: '#fff',
    fontSize: 16,
  },
  bottomBar: {
    ...(Platform.OS === 'web' ? {} : { position: 'absolute' as const, bottom: 0, left: 0, right: 0 }),
    minHeight: 124,
    paddingHorizontal: 24,
    paddingTop: Platform.OS === 'web' ? 20 : 0,
    paddingBottom: Platform.OS === 'web' ? 24 : 36,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 20,
    backgroundColor: Platform.OS === 'web' ? '#000' : 'transparent',
  },
  shutter: {
    width: 76,
    height: 76,
    borderRadius: 38,
    borderWidth: 5,
    borderColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  shutterDisabled: {
    opacity: 0.45,
  },
  shutterInner: {
    width: 58,
    height: 58,
    borderRadius: 29,
    backgroundColor: '#fff',
  },
  actionButton: {
    minWidth: 132,
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 18,
    borderRadius: 24,
  },
  retakeButton: {
    backgroundColor: Platform.OS === 'web' ? '#3F4758' : 'rgba(0,0,0,0.65)',
  },
  confirmButton: {
    backgroundColor: '#4F73E1',
  },
  actionText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});
